import React, { forwardRef } from 'react';

/**
 * FeeReceiptTemplate - A high-fidelity receipt template for manual fee payments.
 * Optimized for both on-screen viewing and PDF generation (using html2canvas + jspdf).
 */
export const FeeReceiptTemplate = forwardRef(({ studentInfo, paymentDetails, collegeName }, ref) => {
  // Use current date as fallback if paymentDate is not provided
  const receiptDate = paymentDetails?.paymentDate || new Date().toISOString();
  
  return (
    <div
      ref={ref}
      className="relative mx-auto w-full max-w-[600px] bg-white p-6 sm:p-8 font-sans shadow-xl border border-gray-200/60 rounded-sm"
      style={{ width: '600px', minHeight: '800px' }}
    >
      {/* Header */}
      <div className="flex flex-col items-center justify-center text-center pb-6 border-b-2 border-dashed border-gray-300">
        <img
          src="/images/CollegeLogo/PCTE.png"
          alt="College Logo"
          className="h-14 object-contain mb-3 grayscale mix-blend-multiply"
          onError={(e) => { e.target.src = 'https://placehold.co/200x80?text=COLLEGE+LOGO'; }}
        />
        <h2 className="text-xl font-bold tracking-tight text-gray-900 uppercase">
          {collegeName || "PCTE Group of Institutes"}
        </h2>
        <p className="mt-1 text-[10px] tracking-widest text-gray-500 uppercase">
          {studentInfo?.course || "Admissions Office"}
        </p>
        <div className="mt-4 border rounded border-gray-800 px-4 py-1.5 text-xs font-bold tracking-widest text-gray-900 uppercase">
          Fee Receipt
        </div>
      </div>

      {/* Meta Information (Date & Receipt No) */}
      <div className="flex justify-between items-center py-4 border-b border-dashed border-gray-200 text-[11px] font-semibold text-gray-600">
        <div>
          <span className="block text-gray-400 uppercase text-[9px] tracking-wider">Date</span>
          <span className="text-gray-800 uppercase">
            {new Date(receiptDate).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          </span>
        </div>
        <div className="text-right">
          <span className="block text-gray-400 uppercase text-[9px] tracking-wider">Receipt No</span>
          <span className="text-gray-800 uppercase">{paymentDetails?.receiptNumber || "PENDING"}</span>
        </div>
      </div>

      {/* Student Details */}
      <div className="py-4 space-y-1 border-b border-dashed border-gray-200">
        <div className="flex justify-between items-start gap-4">
          <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">Student Name</span>
          <span className="text-[11px] font-bold text-gray-800 text-right">{studentInfo?.name || "N/A"}</span>
        </div>
        <div className="flex justify-between items-start gap-4">
          <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">ERP Number</span>
          <span className="text-[11px] font-bold text-gray-800 text-right">{studentInfo?.ERPNumber || "N/A"}</span>
        </div>
        <div className="flex justify-between items-start gap-4">
          <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">Course</span>
          <span className="text-[11px] font-bold text-gray-800 text-right">{studentInfo?.course || "N/A"}</span>
        </div>
        <div className="flex justify-between items-start gap-4">
          <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">Batch</span>
          <span className="text-[11px] font-bold text-gray-800 text-right">{studentInfo?.batch || "N/A"}</span>
        </div>
      </div>

      {/* Payment Method Details */}
      <div className="py-4 space-y-3 border-b border-dashed border-gray-200">
        <div className="flex justify-between items-start gap-4">
          <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">Payment Mode</span>
          <span className="text-[11px] font-bold text-gray-800 text-right uppercase">
            {paymentDetails?.paymentMode || "N/A"}
          </span>
        </div>
        {paymentDetails?.referenceNumber && (
          <div className="flex justify-between items-start gap-4">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">Reference</span>
            <span className="text-[11px] font-bold text-gray-800 text-right uppercase">{paymentDetails.referenceNumber}</span>
          </div>
        )}
        {paymentDetails?.invoiceNumber && (
          <div className="flex justify-between items-start gap-4">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider whitespace-nowrap">Invoice Ref</span>
            <span className="text-[11px] font-bold text-gray-800 text-right uppercase">{paymentDetails.invoiceNumber}</span>
          </div>
        )}
      </div>

      {/* Items & Total */}
      <div className="py-4">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b-2 border-gray-800">
              <th className="py-2 text-[10px] font-bold text-gray-800 uppercase tracking-wider">Description</th>
              <th className="py-2 text-[10px] font-bold text-gray-800 uppercase tracking-wider text-right">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-3 text-[11px] font-semibold text-gray-700 pr-2">
                {paymentDetails?.feeDescription || "Fee Payment Received"}
              </td>
              <td className="py-3 text-[11px] font-bold text-gray-900 text-right font-mono">
                {parseFloat(paymentDetails?.amountPaid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-800">
              <td className="py-3 text-[12px] font-bold text-gray-900 uppercase tracking-widest">Total Paid</td>
              <td className="py-3 text-[14px] font-black text-gray-900 text-right font-mono">
                ₹{parseFloat(paymentDetails?.amountPaid || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Remarks */}
      {paymentDetails?.remarks && (
          <div className="py-3 border-t border-dashed border-gray-200">
            <span className="block text-[9px] text-gray-500 uppercase tracking-wider mb-1">Remarks</span>
            <p className="text-[10px] font-medium text-gray-700 italic">{paymentDetails.remarks}</p>
          </div>
      )}

      {/* Footer Info */}
      <div className="pt-8 border-t border-gray-200 flex justify-end">
        <div className="flex flex-row items-center justify-center text-center space-y-6">
          <div className="text-center">
            <div className="h-[1px] w-40 bg-gray-400 mb-2 mx-auto" />
            <span className="text-[9px] font-bold tracking-widest text-gray-500 uppercase">Authorized Signatory</span>
          </div>
        </div>
      </div>
      
      {/* Disclaimer */}
      <div className="mt-8 pt-4 border-t border-gray-100 text-[8px] text-gray-400 text-center uppercase tracking-tighter">
        This is a computer generated receipt. Valid subject to realization of payment.
      </div>
    </div>
  );
});

FeeReceiptTemplate.displayName = 'FeeReceiptTemplate';
